// src/pages/LoyaltyProgramSetupPage.js
import React, { useState } from 'react';
import { GiftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import Step1ProgramDetails from "../../components/programRegister/Step1ProgramDetails";
import Step2EarningRules from "../../components/programRegister/Step2EarningRules";
import Step3RewardsRedemption from "../../components/programRegister/Step3RewardsRedemption";
import Step4ReviewLaunch from "../../components/programRegister/Step4ReviewLaunch";
import {db} from "../../firebase";
import {useNavigate, useParams} from "react-router-dom";
import {toast} from "react-toastify";

const LoyaltyProgramSetupPage = () => {
    const {userId, businessId} = useParams();
    const navigate = useNavigate();

    const [currentStep, setCurrentStep] = useState(1);
    const [programData, setProgramData] = useState({
        // Step 1: Program Details
        programName: '',
        programDescription: '',
        programType: 'points', // Default to points-based
        startDate: '', // Optional, can default to today or be empty
        endDate: '',     // Optional
        // Step 2: Earning Rules (simplified for points for now)
        pointsPerSpend: '1', // 1 point
        spendAmount: '10',    // per R10 spent
        earningExclusions: '',
        // Step 3: Rewards & Redemption (simplified for points for now)
        rewards: [
            { id: 1, pointsNeeded: '', rewardDescription: '', rewardType: 'discount', value: '' }
        ], // Array of reward objects
        redemptionInstructions: '',
    });

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const totalSteps = 4; // Program Details, Earning Rules, Rewards & Redemption, Review & Launch

    const updateProgramData = (field, value) => {
        setProgramData(prev => ({ ...prev, [field]: value }));
    };

    const updateReward = (id, field, value) => {
        setProgramData(prev => ({
            ...prev,
            rewards: prev.rewards.map(reward =>
                reward.id === id ? { ...reward, [field]: value } : reward
            )
        }));
    };

    const addReward = () => {
        setProgramData(prev => ({
            ...prev,
            rewards: [...prev.rewards, { id: Date.now(), pointsNeeded: '', rewardDescription: '', rewardType: 'discount', value: '' }]
        }));
    };

    const removeReward = (id) => {
        setProgramData(prev => ({
            ...prev,
            rewards: prev.rewards.filter(reward => reward.id !== id)
        }));
    };

    const validateStep = () => {
        setError(''); // Clear previous errors
        switch (currentStep) {
            case 1: // Program Details
                if (!programData.programName || !programData.programDescription || !programData.programType) {
                    setError('Please fill in all required fields for Program Details.');
                    return false;
                }
                break;
            case 2: // Earning Rules
                if (!programData.pointsPerSpend || !programData.spendAmount) {
                    setError('Please define how customers earn points.');
                    return false;
                }
                if (isNaN(programData.pointsPerSpend) || isNaN(programData.spendAmount) || parseFloat(programData.pointsPerSpend) <= 0 || parseFloat(programData.spendAmount) <= 0) {
                    setError('Points per spend and spend amount must be positive numbers.');
                    return false;
                }
                break;
            case 3: // Rewards & Redemption
                if (programData.rewards.length === 0) {
                    setError('Please add at least one reward for your program.');
                    return false;
                }
                for (const reward of programData.rewards) {
                    if (!reward.pointsNeeded || !reward.rewardDescription || !reward.rewardType || !reward.value) {
                        setError('Please complete all fields for each reward, including points needed and value.');
                        return false;
                    }
                    if (isNaN(reward.pointsNeeded) || parseFloat(reward.pointsNeeded) <= 0) {
                        setError('Points needed for each reward must be a positive number.');
                        return false;
                    }
                    if (isNaN(reward.value) || parseFloat(reward.value) <= 0) {
                        setError('Reward value must be a positive number.');
                        return false;
                    }
                }
                break;
            case 4: // Review & Launch (No specific validation here, as it's a summary)
                break;
            default:
                return true;
        }
        return true;
    };

    const handleNext = () => {
        if (validateStep()) {
            if (currentStep < totalSteps) {
                setCurrentStep(prev => prev + 1);
            } else {
                // This is the last step, so perform final submission
                handleLaunchProgram();
            }
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
            setError(''); // Clear error when going back
        }
    };

    const handleLaunchProgram = async () => {
        setLoading(true);
        // This is the final submission function, already validated by handleNext

        const postId = db.ref().push().key

        db.ref().child(`localRewards/programs/${userId}/${businessId}/${postId}`).update({
            programName: programData.programName,
            programDescription: programData.programDescription,
            programType: programData.programType,
            startDate: programData.startDate,
            endDate: programData.endDate,
            pointsPerSpend: programData.pointsPerSpend,
            spendAmount: programData.spendAmount,
            earningExclusions: programData.earningExclusions,
            rewards: programData.rewards,
            redemptionInstructions: programData.redemptionInstructions,
            createdOn: new Date().getTime(),
            businessId: businessId,
            userId: userId,
            postId: postId,
        }).then(() => {
            toast.success(`Loyalty program "${programData.programName}" created successfully!`);
            navigate(`/business-verification/${userId}/${businessId}`)
        }).catch((err) => {
            toast.error(err.message);
        }).finally(() => {
            setLoading(false);
        })
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return <Step1ProgramDetails formData={programData} updateFormData={updateProgramData} />;
            case 2:
                return <Step2EarningRules formData={programData} updateFormData={updateProgramData} />;
            case 3:
                return (
                    <Step3RewardsRedemption
                        formData={programData}
                        updateFormData={updateProgramData}
                        updateReward={updateReward}
                        addReward={addReward}
                        removeReward={removeReward}
                    />
                );
            case 4:
                return <Step4ReviewLaunch formData={programData} />;
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl w-full bg-white rounded-xl shadow-2xl p-8 space-y-8 animate-fade-in-up">
                <div>
                    {/* LocalRewards Logo */}
                    <div className="flex justify-center mb-6">
                        <GiftIcon className="h-16 w-16 text-indigo-600" />
                    </div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Setup Your Loyalty Program
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        Define how your customers will earn rewards and what benefits they'll receive.
                    </p>
                </div>

                {/* Progress Indicator */}
                <div className="flex justify-between items-center relative mb-6 pb-4">
                    <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-200 -z-10 transform -translate-y-1/2"></div>
                    <div
                        className="absolute top-1/2 left-0 h-1 bg-indigo-600 -z-10 transform -translate-y-1/2 transition-all duration-300 ease-in-out"
                        style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
                    ></div>
                    {[...Array(totalSteps)].map((_, index) => (
                        <div key={index} className="flex-1 flex flex-col items-center z-10">
                            <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-2 ${
                                    currentStep >= index + 1 ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-gray-300 text-gray-600'
                                } transition-all duration-300 ease-in-out`}
                            >
                                {index + 1}
                            </div>
                            <div className={`mt-2 text-xs font-medium text-center ${currentStep >= index + 1 ? 'text-indigo-700' : 'text-gray-500'}`}>
                                {index + 1 === 1 && 'Details'}
                                {index + 1 === 2 && 'Earning Rules'}
                                {index + 1 === 3 && 'Rewards'}
                                {index + 1 === 4 && 'Review'}
                            </div>
                        </div>
                    ))}
                </div>


                <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative" role="alert">
                            <strong className="font-bold">Error:</strong>
                            <span className="block sm:inline ml-2">{error}</span>
                        </div>
                    )}

                    {renderStep()}

                    <div className="flex justify-between pt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={handleBack}
                            disabled={currentStep === 1 || loading}
                            className={`inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white ${
                                currentStep === 1 || loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                            }`}
                        >
                            Back
                        </button>
                        <button
                            type="button"
                            onClick={handleNext}
                            className="group relative inline-flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                            disabled={loading}
                        >
                            {currentStep === totalSteps ? (loading ? 'Launching...' : 'Launch Program') : 'Next'}
                            {loading ? (
                                <svg className="animate-spin -mr-1 ml-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <ArrowRightIcon className="-mr-1 ml-2 h-5 w-5 text-indigo-300 group-hover:text-white" aria-hidden="true" />
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LoyaltyProgramSetupPage;